import type { SortingState } from '@tanstack/react-table';
import type { PublishersPageState } from '../stores/publishersPageStore';

type PublishersFilters = PublishersPageState['filters'];

export interface PublishersPageUrlState {
  filters: PublishersFilters;
  sorting: SortingState;
  page: number;
}

const defaultFilters: PublishersFilters = {
  type: 'all',
  kybStatus: 'all',
  hasDapps: 'all',
  ownersOnly: false,
  search: '',
};

const defaultSorting: SortingState = [{ id: 'created', desc: true }];

const publisherTypes = new Set<PublishersFilters['type']>([
  'all',
  'Organization',
  'Individual',
]);

const kybStatuses = new Set<PublishersFilters['kybStatus']>([
  'all',
  'NotStarted',
  'InProgress',
  'UnderReview',
  'Approved',
  'Rejected',
  'RequiresAdditionalInfo',
]);

const dappsFilters = new Set<PublishersFilters['hasDapps']>([
  'all',
  'true',
  'false',
]);

const sortableColumns = new Set([
  'created',
  'updated',
  'name',
  'kybStatus',
  'dappCount',
  'type',
]);

const parsePositiveInteger = (value: string | null) => {
  if (!value) return undefined;

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;

  return parsed;
};

const parseBoolean = (value: string | null) => {
  if (value === 'true') return true;
  if (value === 'false') return false;

  return undefined;
};

const parseSorting = (value: string | null): SortingState | undefined => {
  if (!value) return undefined;

  const [id, direction = 'asc'] = value.split(':');
  if (!sortableColumns.has(id)) return undefined;

  return [{ id, desc: direction === 'desc' }];
};

export const parsePublishersUql = (
  value: string | null,
): PublishersPageUrlState => {
  if (!value) {
    return {
      filters: defaultFilters,
      sorting: defaultSorting,
      page: 1,
    };
  }

  const params = new URLSearchParams(value);
  const filters: PublishersFilters = { ...defaultFilters };
  const type = params.get('type') as PublishersFilters['type'] | null;
  const kybStatus = params.get('kybStatus') as
    | PublishersFilters['kybStatus']
    | null;
  const hasDapps = params.get('hasDapps') as
    | PublishersFilters['hasDapps']
    | null;
  const ownersOnly = parseBoolean(params.get('ownersOnly'));
  const search = params.get('search');

  if (type && publisherTypes.has(type)) filters.type = type;
  if (kybStatus && kybStatuses.has(kybStatus)) {
    filters.kybStatus = kybStatus;
  }
  if (hasDapps && dappsFilters.has(hasDapps)) filters.hasDapps = hasDapps;
  if (ownersOnly !== undefined) filters.ownersOnly = ownersOnly;
  if (search !== null) filters.search = search;

  return {
    filters,
    sorting: parseSorting(params.get('sort')) ?? defaultSorting,
    page: parsePositiveInteger(params.get('page')) ?? 1,
  };
};

export const buildPublishersUql = ({
  filters,
  sorting,
  page,
}: {
  filters: PublishersFilters;
  sorting: SortingState;
  page: number;
}) => {
  const params = new URLSearchParams();

  if (page > 1) params.set('page', String(page));
  if (filters.type !== defaultFilters.type) params.set('type', filters.type);
  if (filters.kybStatus !== defaultFilters.kybStatus) {
    params.set('kybStatus', filters.kybStatus);
  }
  if (filters.hasDapps !== defaultFilters.hasDapps) {
    params.set('hasDapps', filters.hasDapps);
  }
  if (filters.ownersOnly !== defaultFilters.ownersOnly) {
    params.set('ownersOnly', String(filters.ownersOnly));
  }
  if (filters.search !== defaultFilters.search) {
    params.set('search', filters.search);
  }

  const sort = sorting[0];
  if (
    sort &&
    (sort.id !== defaultSorting[0]?.id || sort.desc !== defaultSorting[0]?.desc)
  ) {
    params.set('sort', `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
  }

  return params.toString();
};
